<template>
  <div class="cr-panel">
    <!-- ── Search + Quick actions ─────────────────────── -->
    <div class="cr-toolbar">
      <div class="cr-search">
        <feather-icon icon="SearchIcon" size="14" class="cr-search__icon"/>
        <input
            type="text"
            v-model="search"
            :placeholder="msg('Find an item…')"
            class="cr-search__input"
        />
        <button v-if="search" class="cr-search__clear" @click="search = ''">
          <feather-icon icon="XIcon" size="13"/>
        </button>
      </div>
      <div class="cr-summary">
        <span class="cr-summary__counter">
          <strong>{{ totalMissing }}</strong>
          <span>{{ msg('missing') }}</span>
        </span>
        <span class="cr-summary__counter cr-summary__counter--ok">
          <strong>{{ totalOk }}</strong>
          <span>{{ msg('OK') }}</span>
        </span>
      </div>
    </div>

    <!-- ── Section tabs ─────────────────────────────────── -->
    <div class="cr-tabs">
      <button
          v-for="section in sections"
          :key="section.key"
          class="cr-tab"
          :class="{ active: activeSection === section.key }"
          @click="activeSection = section.key"
      >
        <i :class="section.icon" class="mr-50"></i>
        <span>{{ msg(section.label) }}</span>
        <span v-if="section.missingCount > 0" class="cr-tab__count">{{ section.missingCount }}</span>
      </button>
    </div>

    <!-- ── Item grid ──────────────────────────────────── -->
    <div class="cr-grid" v-if="visibleItems.length">
      <div
          v-for="item in visibleItems"
          :key="item.key"
          class="cr-item"
          :class="{ 'cr-item--missing': (missingResources[item.key] || 0) > 0 }"
      >
        <div class="cr-item__head">
          <div class="cr-item__icon" :style="{ background: iconColor(item.key) }">
            <i :class="item.icon"></i>
          </div>
          <div class="cr-item__name">{{ msg(item.key) }}</div>
        </div>
        <div class="cr-item__counter">
          <button
              type="button"
              class="cr-counter-btn cr-counter-btn--minus"
              :disabled="(missingResources[item.key] || 0) <= 0"
              @click="adjust(item.key, -1)"
              :aria-label="msg('Decrease')"
          >
            <feather-icon icon="MinusIcon" size="14"/>
          </button>
          <input
              type="number"
              min="0"
              :max="itemMax(item.key)"
              :value="missingResources[item.key] || 0"
              @input="onInput(item.key, $event.target.value)"
              class="cr-counter-value"
          />
          <button
              type="button"
              class="cr-counter-btn cr-counter-btn--plus"
              :disabled="(missingResources[item.key] || 0) >= itemMax(item.key)"
              @click="adjust(item.key, 1)"
              :aria-label="msg('Increase')"
          >
            <feather-icon icon="PlusIcon" size="14"/>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="cr-empty">
      <feather-icon icon="SearchIcon" size="22" class="mb-25"/>
      <div>{{ msg('No items match your search.') }}</div>
    </div>
  </div>
</template>


<script>
export default {
  name: "cleaningResources",
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      activeSection: 'kitchen',
      kitchenItems: [
        {key: "platesSmall", icon: "fas fa-plate-wheat"},
        {key: "platesBig", icon: "fas fa-plate-wheat"},
        {key: "smallPan", icon: "fas fa-bacon"},
        {key: "bigPan", icon: "fas fa-bacon"},
        {key: "smallPot", icon: "fas fa-kitchen-set"},
        {key: "bigPot", icon: "fas fa-kitchen-set"},
        {key: "coffeeMachine", icon: "fas fa-coffee"},
        {key: "coffeeCapsules", icon: "fas fa-capsules"},
        {key: "smallChocolates", icon: "fas fa-candy-cane"},
        {key: "forks", icon: "fas fa-utensils"},
        {key: "knives", icon: "fas fa-utensils"},
        {key: "spoon", icon: "fas fa-utensil-spoon"},
        {key: "microwave", icon: "fas fa-microchip"},
        {key: "kettle", icon: "fas fa-beer"},
        {key: "cup", icon: "fas fa-mug-hot"},
        {key: "teacup", icon: "fas fa-coffee"},
      ],
      bathroomItems: [
        {key: "hairdryer", icon: "fas fa-wind"},
        {key: "washingMachine", icon: "fas fa-water"},
        {key: "showerhead", icon: "fas fa-shower"},
        {key: "toiletPaper", icon: "fas fa-toilet-paper"},
        {key: "smallTowel", icon: "fas fa-shower"},
        {key: "bigTowel", icon: "fas fa-shower"},
      ],
      livingRoomItems: [
        {key: "tv", icon: "fas fa-tv"},
        {key: "internet", icon: "fas fa-wifi"},
        {key: "deskChairs", icon: "fas fa-chair"},
        {key: "sofa", icon: "fas fa-couch"},
        {key: "curtains", icon: "fas fa-vectors-square"},
        {key: "rug", icon: "fas fa-rug"},
      ],
      bedroomItems: [
        {key: "bed", icon: "fas fa-bed"},
        {key: "bedlinen", icon: "fas fa-bed"},
        {key: "bedcover", icon: "fas fa-bed"},
        {key: "pillows", icon: "fas fa-bed"},
        {key: "iron", icon: "fas fa-tshirt"},
      ],
    };
  },
  computed: {
    missingResources: {
      get() { return this.value || {}; },
      set(val) { this.$emit('input', val); }
    },
    sections() {
      const counts = {
        kitchen: this.countMissing(this.kitchenItems),
        bathroom: this.countMissing(this.bathroomItems),
        livingRoom: this.countMissing(this.livingRoomItems),
        bedroom: this.countMissing(this.bedroomItems),
      };
      return [
        { key: 'kitchen', label: 'Kitchen', icon: 'fas fa-utensils', missingCount: counts.kitchen },
        { key: 'bathroom', label: 'Bathroom', icon: 'fas fa-shower', missingCount: counts.bathroom },
        { key: 'livingRoom', label: 'Living Room', icon: 'fas fa-couch', missingCount: counts.livingRoom },
        { key: 'bedroom', label: 'Bedroom', icon: 'fas fa-bed', missingCount: counts.bedroom },
      ];
    },
    activeItems() {
      switch (this.activeSection) {
        case 'kitchen': return this.kitchenItems;
        case 'bathroom': return this.bathroomItems;
        case 'livingRoom': return this.livingRoomItems;
        case 'bedroom': return this.bedroomItems;
        default: return [];
      }
    },
    visibleItems() {
      const q = (this.search || '').trim().toLowerCase();
      // When searching, show across all sections; otherwise just the active section
      const pool = q ? [
        ...this.kitchenItems,
        ...this.bathroomItems,
        ...this.livingRoomItems,
        ...this.bedroomItems
      ] : this.activeItems;
      if (!q) return pool;
      return pool.filter(item => {
        const localised = (this.msg(item.key) || item.key).toLowerCase();
        return localised.includes(q) || item.key.toLowerCase().includes(q);
      });
    },
    totalMissing() {
      return this.allItems.reduce((sum, it) => sum + (this.missingResources[it.key] || 0), 0);
    },
    totalOk() {
      return this.allItems.filter(it => !(this.missingResources[it.key] || 0)).length;
    },
    allItems() {
      return [
        ...this.kitchenItems,
        ...this.bathroomItems,
        ...this.livingRoomItems,
        ...this.bedroomItems,
      ];
    },
  },
  methods: {
    countMissing(list) {
      return list.reduce((c, it) => c + ((this.missingResources[it.key] || 0) > 0 ? 1 : 0), 0);
    },
    iconColor(key) {
      // Stable colour-per-section so items stay visually grouped even when filtered
      if (this.kitchenItems.find(i => i.key === key)) return 'rgba(255, 159, 67, 0.15)';
      if (this.bathroomItems.find(i => i.key === key)) return 'rgba(0, 207, 232, 0.15)';
      if (this.livingRoomItems.find(i => i.key === key)) return 'rgba(115, 103, 240, 0.15)';
      if (this.bedroomItems.find(i => i.key === key)) return 'rgba(40, 199, 111, 0.15)';
      return 'rgba(0, 0, 0, 0.05)';
    },
    itemMax(key) {
      // Kitchen items get 200, others 100 (preserves original behavior)
      return this.kitchenItems.find(i => i.key === key) ? 200 : 100;
    },
    adjust(key, delta) {
      const current = this.missingResources[key] || 0;
      const next = Math.min(this.itemMax(key), Math.max(0, current + delta));
      const updated = {...this.missingResources, [key]: next};
      this.$emit('input', updated);
      this.$emit('update:resources', updated);
    },
    onInput(key, raw) {
      const v = parseInt(raw, 10);
      const safe = isNaN(v) ? 0 : Math.min(this.itemMax(key), Math.max(0, v));
      const updated = {...this.missingResources, [key]: safe};
      this.$emit('input', updated);
      this.$emit('update:resources', updated);
    },
    onQuantityChange(key) {
      this.$emit("update:resources", {...this.missingResources});
    },
  },
};
</script>

<style scoped lang="scss">
$cr-radius: 12px;
$cr-radius-lg: 14px;
$cr-border: rgba(60, 60, 70, 0.08);
$cr-border-dark: rgba(255, 255, 255, 0.06);
$cr-primary: #7367f0;
$cr-success: #28c76f;
$cr-warning: #ff9f43;
$cr-danger: #ea5455;

.cr-panel { padding: 4px; }

/* ── Toolbar ─────────────────────────────────────── */
.cr-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.cr-search {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid $cr-border;
  border-radius: 999px;
  padding: 0 12px;
  height: 36px;
  transition: all 0.18s ease;

  &:focus-within {
    border-color: $cr-primary;
    background: rgba(115, 103, 240, 0.04);
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.cr-search__icon { color: var(--secondary); flex-shrink: 0; margin-right: 8px; }

.cr-search__input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  color: var(--text);
  min-width: 0;
}

.cr-search__clear {
  width: 22px; height: 22px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: var(--secondary);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;

  &:hover { background: $cr-danger; color: white; }
}

.cr-summary {
  display: flex;
  gap: 8px;
}

.cr-summary__counter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(234, 84, 85, 0.1);
  color: $cr-danger;
  font-size: 0.78rem;
  font-weight: 700;

  strong { font-size: 0.9rem; font-weight: 800; }

  &--ok {
    background: rgba(40, 199, 111, 0.1);
    color: $cr-success;
  }
}

/* ── Tabs ──────────────────────────────────────── */
.cr-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  padding: 6px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid $cr-border;
  border-radius: $cr-radius;
  flex-wrap: wrap;
}

.cr-tab {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--secondary);
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;

  i { font-size: 0.9rem; }

  &:hover { background: rgba(115, 103, 240, 0.08); color: $cr-primary; }

  &.active {
    background: linear-gradient(135deg, $cr-primary 0%, #5e50ee 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.32);
  }
}

.cr-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: $cr-danger;
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
}

/* ── Item grid ─────────────────────────────────── */
.cr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.cr-item {
  background: var(--card-bg, white);
  border: 1px solid $cr-border;
  border-radius: $cr-radius-lg;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.18s ease;

  &:hover {
    border-color: rgba(115, 103, 240, 0.3);
    box-shadow: 0 6px 16px rgba(34, 41, 47, 0.06);
    transform: translateY(-1px);
  }

  &--missing {
    border-color: rgba(234, 84, 85, 0.35);
    background: rgba(234, 84, 85, 0.03);

    .cr-item__icon { background: rgba(234, 84, 85, 0.15) !important; color: $cr-danger; }
  }
}

.cr-item__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cr-item__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
  color: var(--text);
}

.cr-item__name {
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  text-transform: capitalize;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cr-item__counter {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  padding: 3px;
}

.cr-counter-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: $cr-primary;
    color: white;
  }

  &--minus:hover:not(:disabled) {
    background: $cr-warning;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.cr-counter-value {
  flex: 1;
  width: 100%;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--text);
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;
  min-width: 0;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.cr-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--secondary);
  font-size: 0.86rem;
  border: 1.5px dashed $cr-border;
  border-radius: $cr-radius-lg;
}

/* ── Dark mode ────────────────────────────────────── */
.dark-layout {
  .cr-search,
  .cr-tabs,
  .cr-item,
  .cr-empty { border-color: $cr-border-dark; }
  .cr-search { background: rgba(255, 255, 255, 0.04); }
  .cr-search__input { color: rgba(255, 255, 255, 0.95); }
  .cr-search__clear { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.6); }
  .cr-tabs { background: rgba(255, 255, 255, 0.03); }
  .cr-tab { color: rgba(255, 255, 255, 0.65); }
  .cr-item {
    background: #283046;

    &__name { color: rgba(255, 255, 255, 0.95); }
  }
  .cr-item--missing {
    background: rgba(234, 84, 85, 0.08);
    border-color: rgba(234, 84, 85, 0.5);
  }
  .cr-item__counter { background: rgba(255, 255, 255, 0.06); }
  .cr-counter-value { color: rgba(255, 255, 255, 0.95); }
  .cr-counter-btn { color: rgba(255, 255, 255, 0.6); }
  .cr-empty { color: rgba(255, 255, 255, 0.5); }
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 600px) {
  .cr-toolbar { flex-direction: column; align-items: stretch; }
  .cr-grid { grid-template-columns: 1fr 1fr; }
  .cr-tab { padding: 6px 10px; font-size: 0.76rem; }
}
</style>
